import { useState, useEffect, useCallback } from "react";
import { Copy, Download, Loader2 } from "lucide-react";
import { Shimmer, TableShimmer } from "@/components/ui/shimmer";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { DataTablePagination } from "@/components/ui/data-table-pagination";
import TransactionFiltersPanel, {
  getDefaultFilters,
  type FilterValues,
} from "@/components/common/TransactionFiltersPanel";
import { useAuth } from "@/context/AuthContext";
import {
  getTransactions,
  getAllTransactions,
  type WalletTransactionRow,
  type TransactionFilters,
} from "@/utils/wallet-api";
import { exportTransactionsToExcel } from "@/utils/export-excel";

// Payment gateway types that constitute "invoices"
const INVOICE_TYPES = [
  "paid_via_bank",
  "paid_via_cashfree",
  "paid_via_gpay",
  "paid_via_kotak",
  "paid_via_neokred",
  "paid_via_openbank",
  "paid_via_openbank_neft",
  "paid_via_payu",
  "paid_via_phonepe",
  "paid_via_razorpay",
  "paid_via_sabpaisa",
  "paid_via_stripe",
  "paid_via_subspace",
  "paid_via_user",
  "paid_via_zwitch",
  "paid_via_zwtich",
];

function formatINR(paise: number): string {
  const rupees = paise / 100;
  const prefix = paise >= 0 ? "+" : "";
  return prefix + "₹" + Math.abs(rupees).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function formatType(type: string): string {
  return type.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

export default function InvoicesPage() {
  const { userId, token } = useAuth();

  const [rows, setRows] = useState<WalletTransactionRow[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState("");

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [filters, setFilters] = useState<FilterValues>(getDefaultFilters);

  const buildFilters = useCallback((): TransactionFilters => {
    const txnFilters: TransactionFilters = {
      userId: userId!,
      types: INVOICE_TYPES,
    };
    if (filters.createdFrom) txnFilters.createdFrom = filters.createdFrom.startOf("day").toISOString();
    if (filters.createdTo) txnFilters.createdTo = filters.createdTo.endOf("day").toISOString();
    if (filters.search.trim()) txnFilters.search = filters.search.trim();
    return txnFilters;
  }, [userId, filters]);

  const fetchData = useCallback(async () => {
    if (!userId || !token) return;

    setLoading(true);
    setError("");

    try {
      const result = await getTransactions(buildFilters(), rowsPerPage, page * rowsPerPage, token);
      setRows(result.rows);
      setTotal(result.total);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load invoices");
    } finally {
      setLoading(false);
    }
  }, [userId, token, page, rowsPerPage, buildFilters]);

  useEffect(() => { fetchData(); }, [fetchData]);
  useEffect(() => { setPage(0); }, [filters]);

  const handleExport = async () => {
    if (!userId || !token) return;
    setExporting(true);
    try {
      const allRows = await getAllTransactions(buildFilters(), token);
      exportTransactionsToExcel(allRows, "invoices");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Export failed");
    } finally {
      setExporting(false);
    }
  };

  const copyId = (id: string) => { navigator.clipboard.writeText(id); };

  return (
    <div>
      <div className="mb-8">
        <h1 className="text-3xl font-bold">
          Invoices
        </h1>
        <p className="text-muted-foreground">
          All wallet top-up transactions via payment gateways.
        </p>
      </div>

      <Shimmer loading={loading} fallback={<TableShimmer />}>
        <Card>
          <CardContent className="p-5">
            <div className="flex items-center justify-between flex-wrap gap-2 mb-4">
              <div>
                <h3 className="text-base font-semibold">Payment Invoices</h3>
                <span className="text-sm text-muted-foreground">
                  {total.toLocaleString()} invoices total
                </span>
              </div>
            </div>

            <TransactionFiltersPanel
              filters={filters}
              onChange={setFilters}
              availableTypes={[]}
              hideTypeFilter
              actions={
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleExport}
                  disabled={exporting || total === 0}
                >
                  {exporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
                  {exporting ? "Exporting..." : "Export Excel"}
                </Button>
              }
            />

            {error && <Alert variant="destructive" className="mb-4"><AlertDescription>{error}</AlertDescription></Alert>}

            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="font-semibold">Transaction ID</TableHead>
                  <TableHead className="font-semibold">Purpose</TableHead>
                  <TableHead className="font-semibold">Payment Method</TableHead>
                  <TableHead className="font-semibold text-right">Amount</TableHead>
                  <TableHead className="font-semibold">Created At</TableHead>
                  <TableHead className="font-semibold">Updated At</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center py-12">
                      <span className="text-muted-foreground">No invoices found</span>
                    </TableCell>
                  </TableRow>
                ) : (
                  rows.map((txn) => (
                    <TableRow key={txn.id}>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          <span className="text-xs text-muted-foreground font-mono">
                            {txn.id.slice(0, 8)}...
                          </span>
                          <Button variant="ghost" size="icon-xs" onClick={() => copyId(txn.id)}>
                            <Copy className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      </TableCell>
                      <TableCell>
                        <span className="text-sm max-w-[260px] truncate block" title={txn.purpose}>
                          {txn.purpose}
                        </span>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className="border-green-500 text-green-600 dark:text-green-400">{formatType(txn.type)}</Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <span className="text-sm font-semibold text-green-600 dark:text-green-400">
                          {formatINR(txn.amount)}
                        </span>
                      </TableCell>
                      <TableCell>
                        <span className="text-xs text-muted-foreground">{formatDate(txn.created_at)}</span>
                      </TableCell>
                      <TableCell>
                        <span className="text-xs text-muted-foreground">{formatDate(txn.updated_at)}</span>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>

            <DataTablePagination
              count={total}
              page={page}
              rowsPerPage={rowsPerPage}
              onPageChange={(p) => setPage(p)}
              onRowsPerPageChange={(rpp) => { setRowsPerPage(rpp); setPage(0); }}
            />
          </CardContent>
        </Card>
      </Shimmer>
    </div>
  );
}
